import { useEffect, type RefObject } from 'react';
import { ui, useUi } from '../../store/ui';

/** How far a finger moves before the touch counts as a swipe or a scroll. */
const SLOP = 10;
/** Share of the width a slow drag has to cover to switch panes. */
const COMMIT = 0.3;
/** A flick this fast (px per ms) switches panes however short it is. */
const FLICK = 0.4;

/** True when the touched element, or one of its parents inside `root`, can still scroll sideways the way the finger moves. */
function scrollsSideways(target: Element | null, root: Element, dx: number): boolean {
  for (let el = target; el && el !== root; el = el.parentElement) {
    if (el.scrollWidth <= el.clientWidth + 1) continue;
    const overflow = getComputedStyle(el).overflowX;
    if (overflow !== 'auto' && overflow !== 'scroll') continue;
    if (dx > 0 ? el.scrollLeft > 0 : el.scrollLeft + el.clientWidth < el.scrollWidth - 1) return true;
  }
  return false;
}

/**
 * Phones: swipe right on a conversation to slide it aside and show the channel list underneath, swipe left on the
 * list to bring the conversation back, and swipe right on a side panel (members, thread, search) to close it. The
 * conversation follows the finger while dragging; letting go settles it on whichever pane it is closer to.
 */
export function useMobileSwipe(stage: RefObject<HTMLElement | null>, main: RefObject<HTMLElement | null>, enabled: boolean): void {
  useEffect(() => {
    const root = stage.current;
    const layer = main.current;
    if (!enabled || !root || !layer) return;

    let gesture: { x: number; y: number; target: Element | null; pane: 'nav' | 'main' | 'side'; dragging: boolean } | null = null;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;

    const reset = () => {
      // Hands the position back to the layer's classes, which animate it to wherever the pane now is.
      layer.style.translate = '';
      layer.style.transition = '';
      gesture = null;
    };

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        if (gesture?.dragging) reset();
        gesture = null;
        return;
      }
      const t = e.touches[0]!;
      gesture = { x: t.clientX, y: t.clientY, target: e.target as Element | null, pane: useUi.getState().mobilePane, dragging: false };
      lastX = t.clientX;
      lastT = e.timeStamp;
      velocity = 0;
    };

    const onMove = (e: TouchEvent) => {
      if (!gesture) return;
      const t = e.touches[0]!;
      const dx = t.clientX - gesture.x;
      const dy = t.clientY - gesture.y;
      if (!gesture.dragging) {
        if (Math.hypot(dx, dy) < SLOP) return;
        const sideways = Math.abs(dx) > Math.abs(dy) * 1.2;
        const towardOtherPane = gesture.pane === 'nav' ? dx < 0 : dx > 0;
        const selecting = window.getSelection()?.type === 'Range';
        if (!sideways || !towardOtherPane || !e.cancelable || selecting || scrollsSideways(gesture.target, root, dx)) {
          gesture = null;
          return;
        }
        gesture.dragging = true;
        layer.style.transition = 'none';
      }
      e.preventDefault();
      if (e.timeStamp > lastT) velocity = 0.7 * ((t.clientX - lastX) / (e.timeStamp - lastT)) + 0.3 * velocity;
      lastX = t.clientX;
      lastT = e.timeStamp;
      // A side panel just closes when let go; the conversation only follows the finger between it and the list.
      if (gesture.pane === 'side') return;
      const width = root.clientWidth;
      const offset = Math.min(width, Math.max(0, gesture.pane === 'nav' ? width + dx : dx));
      layer.style.translate = `${offset}px 0`;
    };

    const onEnd = () => {
      if (!gesture?.dragging) {
        gesture = null;
        return;
      }
      const { pane, x } = gesture;
      const dx = lastX - x;
      const flick = Math.abs(velocity) > FLICK && Math.sign(velocity) === Math.sign(dx);
      const commit = flick || Math.abs(dx) > root.clientWidth * COMMIT;
      reset();
      if (!commit) return;
      if (pane === 'nav') ui.setMobilePane('main');
      else if (pane === 'main') ui.setMobilePane('nav');
      else ui.setRightPanel(null);
    };

    const onCancel = () => {
      if (gesture?.dragging) reset();
      gesture = null;
    };

    root.addEventListener('touchstart', onStart, { passive: true });
    // Not passive: once a touch is a sideways swipe, the page must not scroll with it.
    root.addEventListener('touchmove', onMove, { passive: false });
    root.addEventListener('touchend', onEnd);
    root.addEventListener('touchcancel', onCancel);
    return () => {
      root.removeEventListener('touchstart', onStart);
      root.removeEventListener('touchmove', onMove);
      root.removeEventListener('touchend', onEnd);
      root.removeEventListener('touchcancel', onCancel);
      layer.style.translate = '';
      layer.style.transition = '';
    };
  }, [stage, main, enabled]);
}
